// src/components/users/UserFormDialog.tsx

import React, { useCallback, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button, Dialog, DialogContent, TextField, Typography, DialogActions,
  DialogTitle, Stack, CircularProgress, Alert, Box, FormControl,
  InputLabel, Select, MenuItem, IconButton, InputAdornment
} from '@mui/material';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { PersonAdd, Edit, Visibility, VisibilityOff } from '@mui/icons-material';

// --- Tus hooks, tipos y constantes ---
import { useUserCore } from '../../../core/modules/user';
import { useGlobalCore } from '../../../core/modules/global';
import { useCompanyCore } from '../../../core/modules/company';
import { User } from '../../../core/interfaces/user.interface';
import { PostUserPayloadRequest } from '../../../core/modules/user/interfaces/post-user';
import { PutUserPayload } from '../../../core/modules/user/interfaces/put-user';
import { CompanyRole } from '../../../core/modules/user/interfaces/role';

// --- Props del Componente ---
interface UserFormDialogProps {
  userToEdit: User | null;
  onSuccess: () => void;
}

// --- Definición de los datos del formulario ---
interface FormData {
  fullName: string;
  email: string;
  role: CompanyRole;
  password?: string; // La contraseña es opcional, solo requerida al crear
}

// --- Esquema de Validación Dinámico ---
const schema = (isEditing: boolean) => yup.object().shape({
  fullName: yup.string().required('El nombre completo es requerido.'),
  email: yup.string().email('Debe ser un email válido.').required('El email es requerido.'),
  role: yup.string().oneOf(Object.values(CompanyRole)).required('Debes seleccionar un rol.'),
  // La contraseña solo es requerida si NO estamos editando
  password: yup.string().when([], { // Usamos un array vacío para que siempre se evalúe
      is: () => !isEditing,
      then: (s) => s.required('La contraseña es requerida.').min(8, 'La contraseña debe tener al menos 8 caracteres.'),
      otherwise: (s) => s.optional(),
  }),
});

const UserFormDialog = ({ userToEdit, onSuccess }: UserFormDialogProps) => {
  const { t } = useTranslation();
  const { dialog, closeDialog, showSnackbar } = useGlobalCore();
  const { postUser, putUser } = useUserCore();
  const { company } = useCompanyCore(); // Para obtener el company.id

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const isEditing = !!userToEdit;

  const { control, formState, handleSubmit, reset } = useForm<FormData>({
    mode: 'onChange',
    resolver: yupResolver(schema(isEditing)), // Pasamos si estamos editando al schema
    defaultValues: {
      fullName: '',
      email: '',
      role: CompanyRole.USER,
      password: '',
    },
  });
  const { isValid, errors, isDirty } = formState;

  // Poblar el formulario cuando se abre o cambia el usuario a editar
  useEffect(() => {
    if (dialog === 'CREATE_OR_EDIT_USER_DIALOG') {
      if (isEditing && userToEdit) {
        reset({
          fullName: userToEdit.full_name || '',
          email: userToEdit.email,
          role: userToEdit.role,
        });
      } else {
        reset({ fullName: '', email: '', role: CompanyRole.USER, password: '' });
      }
    }
  }, [isEditing, userToEdit, dialog, reset]);

  const handleClose = () => {
    setApiError(null);
    closeDialog();
  };

  const onSubmit: SubmitHandler<FormData> = useCallback(
    async (formData) => {
      if (!company?.id) return;
      
      setIsSubmitting(true);
      setApiError(null);

      const commonOnSuccess = () => {
        showSnackbar({ 
          message: `Usuario ${isEditing ? 'actualizado' : 'creado'} con éxito.`, 
          options: { variant: 'success' } 
        });
        onSuccess();
        handleClose();
      };
      
      const commonOnFailure = (error: any) => {
        const message = error.response?.data?.detail || 'Ocurrió un error inesperado.';
        setApiError(message);
      };

      try {
        if (isEditing && userToEdit) {
          const payload: PutUserPayload = {
            fullName: formData.fullName,
            email: formData.email,
            role: formData.role,
          };
          await putUser(userToEdit.id, payload, commonOnSuccess, commonOnFailure);
        } else {
          const payload: PostUserPayloadRequest = {
            name: formData.fullName,
            email: formData.email,
            password: formData.password!,
            role: formData.role,
            companyId: company.id
          };
          await postUser(payload, commonOnSuccess, commonOnFailure);
        }
      } catch (error) {
        commonOnFailure(error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [isEditing, userToEdit, company?.id, postUser, putUser, onSuccess, showSnackbar, handleClose]
  );

  return (
    <Dialog open={dialog === 'CREATE_OR_EDIT_USER_DIALOG'} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>
        <Typography variant="h6" component="div" className="font-bold flex items-center gap-2">
          {isEditing ? <Edit /> : <PersonAdd />}
          {isEditing ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
        </Typography>
      </DialogTitle>
      
      <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {apiError && <Alert severity="error">{apiError}</Alert>}

            <Controller name="fullName" control={control} render={({ field }) => (
              <TextField {...field} label="Nombre Completo" error={!!errors.fullName} helperText={errors.fullName?.message} fullWidth required autoFocus />
            )} />

            <Controller name="email" control={control} render={({ field }) => (
              <TextField {...field} label="Email" type="email" error={!!errors.email} helperText={errors.email?.message} required fullWidth />
            )} />
            
            <FormControl fullWidth>
              <InputLabel id="role-select-label">Rol *</InputLabel>
              <Controller name="role" control={control} render={({ field }) => (
                <Select {...field} labelId="role-select-label" label="Rol *">
                  {Object.values(CompanyRole).map((roleValue) => (
                    <MenuItem key={roleValue} value={roleValue}>{roleValue}</MenuItem>
                  ))}
                </Select>
              )} />
            </FormControl>

            {!isEditing && (
              <Controller name="password" control={control} render={({ field }) => (
                <TextField {...field} label="Contraseña" type={showPassword ? 'text' : 'password'} error={!!errors.password} helperText={errors.password?.message} required fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )} />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleClose} disabled={isSubmitting}>Cancelar</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isDirty || !isValid || isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isSubmitting ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

export default UserFormDialog;